var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useLanguage } from '@base/LanguageProvider';
import * as baobab from '@data/baobab/baobab';
import { createDroppableId, DroppableType, parseDroppableId } from '@data/dragAndDrop/types';
import { useTagContext } from '@data/tag/tagContext';
import { ContextMenu } from '@shared/contextmenu';
import { appColors } from '@styled/useTheme';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useLocalData } from '../../base/LocalDataContext';
import { useActiveYears } from '../../data/hooks/timePoints';
import { useActiveVersions, useShownVersions } from '../../data/hooks/versions';
import { getWithDataCompoundExpression, getFilterCompoundExpression } from '../../data/sheetRow/sheetRow';
import { useActionsBySheetRowId } from '../../data/sheetRow/sheetRowActions';
import { useSelectedSheetRow, useSheetRowActions, useSheetRowPath, useSheetRowsById, } from '../../data/sheetRow/sheetRowHooks';
import { useAuth } from '../../providers/AuthProvider';
import { getMenuConfig } from './createMenuItems';
import { Column } from './FinderColumn';
import { useFinder } from './useFinder';
import { useLockRows } from './useLockUnlock';
var getListStyle = function (isDraggingOver) { return ({
    background: isDraggingOver ? appColors.primary.w50 : 'transparent',
}); };
export default function Finder(_a) {
    var _b;
    var expandFinder = _a.expandFinder;
    var finderRef = useRef(null);
    var finderContainerRef = useRef(null);
    var _c = __read(useState(true), 2), canManipulate = _c[0], setCanManipulate = _c[1];
    var _d = __read(useState(null), 2), menuTarget = _d[0], setMenuTarget = _d[1];
    var _e = __read(useState(null), 2), copyItem = _e[0], setCopyItem = _e[1];
    var L = useLanguage().L;
    var navigate = useNavigate();
    var _f = useAuth(), user = _f.user, settings = _f.settings;
    var tagData = useTagContext().tagData;
    var selectedSheetRow = useSelectedSheetRow();
    var activeYears = useActiveYears();
    var activeVersions = useActiveVersions();
    var _g = useSheetRowActions(), duplicateSheetRow = _g.duplicateSheetRow, materialize = _g.materialize;
    var shownVersions = useShownVersions(settings);
    var _h = useLockRows(), lockRows = _h.lockRows, lockRowsAndSubRows = _h.lockRowsAndSubRows;
    var sheetRowsById = useSheetRowsById();
    var sheetRowPath = useSheetRowPath();
    var actionsMap = useActionsBySheetRowId().data;
    var _j = useLocalData(), withDataMap = _j.withDataMap, filterTagIds = _j.filterTagIds;
    var interval = { start: settings.model_table_interval_start, end: settings.model_table_interval_end };
    var withDataCompoundExpression = getWithDataCompoundExpression(settings.model_table_timeline_versions_by_year, interval, activeYears, tagData.tagById, tagData.internalTagsMap, shownVersions, activeVersions);
    var filterCompoundExpression = getFilterCompoundExpression(filterTagIds, tagData.tagById);
    var finderColumns = useFinder(sheetRowPath, { tagMap: tagData.tagById, sheetRowMap: sheetRowsById }, { withDataCompoundExpression: withDataCompoundExpression, filterCompoundExpression: filterCompoundExpression }, withDataMap);
    useEffect(function () {
        var scrollHandler = function () {
            setTimeout(function () {
                if (finderContainerRef.current !== null && finderRef.current !== null) {
                    finderContainerRef.current.scrollTo(finderRef.current.offsetWidth, 0);
                }
            }, 10);
        };
        scrollHandler();
    }, [finderColumns]);
    var selectedClsDroppableId = selectedSheetRow
        ? createDroppableId({
            type: DroppableType.Finder,
            sheetRowPath: selectedSheetRow === null || selectedSheetRow === void 0 ? void 0 : selectedSheetRow.path,
        })
        : null;
    var canDropInEmptySpace = useMemo(function () {
        var _a;
        var parentPath = '';
        if (selectedSheetRow === null || selectedSheetRow === void 0 ? void 0 : selectedSheetRow.path) {
            var pathArray = selectedSheetRow.path.split('_');
            if (pathArray.length > 1) {
                pathArray.pop();
                parentPath = pathArray.join('_');
            }
        }
        var lastFinderColPath = (_a = finderColumns[finderColumns.length - 1]) === null || _a === void 0 ? void 0 : _a.path;
        if (lastFinderColPath === parentPath) {
            return true;
        }
        return false;
    }, [selectedSheetRow === null || selectedSheetRow === void 0 ? void 0 : selectedSheetRow.path, (_b = finderColumns[finderColumns.length - 1]) === null || _b === void 0 ? void 0 : _b.path, sheetRowsById]);
    if (!user) {
        return null;
    }
    var setTarget = function (parentId) {
        return function (id, path, isGenerated, parentTagId, generatedTagId) {
            if (isGenerated === void 0) { isGenerated = false; }
            setMenuTarget({ parentId: parentId, id: id, path: path, isGenerated: isGenerated, parentTagId: parentTagId, generatedTagId: generatedTagId });
        };
    };
    var setColumnTarget = function (parentId, path) {
        setMenuTarget(parentId ? { id: parentId, path: path } : { id: null, parentId: parentId, path: '0' });
        setCanManipulate(false);
    };
    var setGlobalMenuTarget = function (e) {
        e.stopPropagation();
        setMenuTarget(selectedSheetRow
            ? {
                id: selectedSheetRow.sheetRow.id,
                ownerId: undefined,
                parentId: selectedSheetRow.sheetRow.parentId,
                path: selectedSheetRow.path,
                generatedTagId: selectedSheetRow.generatedTagId,
            }
            : null);
        setCanManipulate(false);
    };
    var columnClickHandler = function (column, e) {
        baobab.root.set(['state', 'selectedRowPath'], null);
        if (e.defaultPrevented) {
            return; // do nothing if event is triggered by dnd
        }
        expandFinder();
        navigate("/sheet/".concat(column.path));
    };
    var menuItems = getMenuConfig(menuTarget, canManipulate, copyItem, setCopyItem, user, tagData, sheetRowsById, duplicateSheetRow, lockRows, lockRowsAndSubRows, L, finderColumns, user.classActions, function () {
        if (menuTarget) {
            materialize(menuTarget === null || menuTarget === void 0 ? void 0 : menuTarget.path);
        }
    }, actionsMap);
    return (React.createElement("div", { className: "column-view-container unselectable", ref: finderContainerRef, onContextMenu: setGlobalMenuTarget },
        React.createElement("div", { className: "column-view-composition", ref: finderRef },
            finderColumns.map(function (c) {
                var droppableId = createDroppableId({
                    type: DroppableType.Finder,
                    sheetRowPath: c.path,
                });
                return (React.createElement(Droppable, { key: droppableId, droppableId: droppableId, mode: "virtual", isCombineEnabled: true, renderClone: function (provided, snapshot, rubric) {
                        var item = c.items.find(function (i, idx) { return rubric.source.index === idx; });
                        return (React.createElement("ul", { className: "column" },
                            React.createElement("li", __assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { className: "active ".concat((item === null || item === void 0 ? void 0 : item.isGenerated) ? ' generated' : '') }),
                                React.createElement("span", { className: "title" }, item === null || item === void 0 ? void 0 : item.label))));
                    } }, function (provided, snapshot) {
                    var targetParsed = (snapshot === null || snapshot === void 0 ? void 0 : snapshot.draggingOverWith)
                        ? parseDroppableId(snapshot.draggingOverWith)
                        : null;
                    var itemParsed = (targetParsed === null || targetParsed === void 0 ? void 0 : targetParsed.sourceDroppableId)
                        ? parseDroppableId(targetParsed.sourceDroppableId)
                        : null;
                    var isFromSidebar = (itemParsed && itemParsed.type === DroppableType.TagSidebar) || false;
                    var isFromFinder = (itemParsed && itemParsed.type === DroppableType.Finder) || false;
                    var onColumnDragActive = snapshot.isDraggingOver && (isFromSidebar || isFromFinder);
                    return (React.createElement("ul", __assign({ ref: provided.innerRef }, provided.droppableProps, { className: "column", style: getListStyle(onColumnDragActive), onClick: function (e) { return columnClickHandler(c, e); }, onContextMenu: function (e) {
                            e.stopPropagation();
                            setColumnTarget(c.clusterId, c.path);
                        } }),
                        React.createElement(Column, { key: droppableId, column: c, menuTarget: menuTarget, isUsingPlaceholder: snapshot.isUsingPlaceholder, providedInnerRef: provided.innerRef, setParentHandler: setTarget, expandFinder: expandFinder })));
                }));
            }),
            canDropInEmptySpace && selectedClsDroppableId ? (React.createElement(Droppable, { key: selectedClsDroppableId, droppableId: selectedClsDroppableId, mode: "virtual", renderClone: function (provided, snapshot, rubric) {
                    return React.createElement("ul", { className: "column" });
                } }, function (provided, snapshot) {
                var targetParsed = (snapshot === null || snapshot === void 0 ? void 0 : snapshot.draggingOverWith)
                    ? parseDroppableId(snapshot.draggingOverWith)
                    : null;
                var itemParsed = (targetParsed === null || targetParsed === void 0 ? void 0 : targetParsed.sourceDroppableId)
                    ? parseDroppableId(targetParsed.sourceDroppableId)
                    : null;
                var isFromSidebar = (itemParsed && itemParsed.type === DroppableType.TagSidebar) || false;
                var isFromFinder = (itemParsed && itemParsed.type === DroppableType.Finder) || false;
                var onColumnDragActive = snapshot.isDraggingOver && (isFromSidebar || isFromFinder);
                return (React.createElement("ul", __assign({ ref: provided.innerRef }, provided.droppableProps, { className: "column", style: __assign(__assign({}, getListStyle(onColumnDragActive)), { flexGrow: 1 }) })));
            })) : null),
        React.createElement(ContextMenu, { items: menuItems, triggerRefs: [{ ref: finderContainerRef }], onClose: function () {
                setMenuTarget(null);
                setCanManipulate(true);
            } })));
}
